import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SiteEntity } from '../models/site.entity';
import { AuthService } from '../services/auth.service';
import { GlobalStateService } from '../services/global-state.service';

export function hasStorefrontEntitlement(site: SiteEntity | null | undefined): boolean {
  const entitlement = site?.['license']?.active_for_storefront;
  return entitlement === true || entitlement === 1 || entitlement === '1' || entitlement === 'true';
}

/**
 * Resolve the authoritative site for entitlement checks.
 *
 * The currently-selected site (from /shop/sites or global state) does NOT carry the
 * `license` relation, whereas the client's sites (from /shop/client) do. So we map the
 * selected site's uniq onto the client's licensed site, falling back to the selection
 * itself, then the first client site.
 */
export function resolveLicensedSite(
  clientSites: SiteEntity[] | null | undefined,
  selectedSite: SiteEntity | null | undefined,
): SiteEntity | undefined {
  const sites = clientSites ?? [];
  return selectedSite
    ? sites.find((site) => site.uniq === selectedSite.uniq) ?? selectedSite
    : sites[0];
}

/** Whether the client currently has an active Web Storefront for the selected site. */
export function clientHasStorefrontAccess(
  clientSites: SiteEntity[] | null | undefined,
  selectedSite: SiteEntity | null | undefined,
): boolean {
  return hasStorefrontEntitlement(resolveLicensedSite(clientSites, selectedSite));
}

export const storefrontEntitlementGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const globalState = inject(GlobalStateService);
  const router = inject(Router);

  const clientSites = (authService.getClient()?.sites ?? []) as SiteEntity[];
  return clientHasStorefrontAccess(clientSites, globalState.getCurrentSite())
    ? true
    : router.createUrlTree(['/']);
};
